import {
  isDiarizationProvider,
  isEnsembleMergeMode,
  isTranscriptionMode,
  type TDiarizationProvider,
  type TEnsembleMergeMode,
  TRANSCRIPTION_LABELS,
  type TTranscriptionMode,
} from "@lexicanon/shared"

/**
 * Resolve the workspace's speaker-turns provider the way the backend does:
 * org `diarization_provider` → platform `default_diarization_provider` → "deepgram".
 * Pure so it unit-tests without React or the API client.
 */
export function resolveWorkspaceDiarizationProvider(
  settings: Record<string, string> | undefined,
): TDiarizationProvider {
  const org = settings?.diarization_provider
  if (org && isDiarizationProvider(org)) return org
  const platform = settings?.default_diarization_provider
  if (platform && isDiarizationProvider(platform)) return platform
  return "deepgram"
}

/**
 * org `transcription_mode` → platform `default_transcription_mode` → "ensemble".
 * Floor matches backend `DEFAULT_TRANSCRIPTION_MODE` (flipped 2026-09-06).
 */
export function resolveWorkspaceTranscriptionMode(settings: Record<string, string> | undefined): TTranscriptionMode {
  const org = settings?.transcription_mode
  if (org && isTranscriptionMode(org)) return org
  const platform = settings?.default_transcription_mode
  if (platform && isTranscriptionMode(platform)) return platform
  return "ensemble"
}

/**
 * org `ensemble_merge_mode` → platform `default_ensemble_merge_mode` → "constrained".
 */
export function resolveWorkspaceMergeMode(settings: Record<string, string> | undefined): TEnsembleMergeMode {
  const org = settings?.ensemble_merge_mode
  if (org && isEnsembleMergeMode(org)) return org
  const platform = settings?.default_ensemble_merge_mode
  if (platform && isEnsembleMergeMode(platform)) return platform
  return "constrained"
}

export interface IEnsembleEngineCredits {
  provider: string
  credits: number
}

export interface IEnsembleEstimateBreakdown {
  wordEngines: IEnsembleEngineCredits[]
  turnEngines: IEnsembleEngineCredits[]
  adjudication: number
  canRemerge: boolean
}

/**
 * Tenant-facing credits line for the ensemble estimate: "Soniox ~12 cr · MAI-Transcribe-2 (Azure) ~10 cr · …".
 * Never money.
 */
function engineLabel(provider: string): string {
  for (const [id, label] of Object.entries(TRANSCRIPTION_LABELS)) {
    if (id === provider) return label
  }
  return provider
}

export function formatEnsembleCreditsLine(ensemble: IEnsembleEstimateBreakdown): string {
  const engines = [...ensemble.wordEngines, ...ensemble.turnEngines]
  const parts = engines.map((row) => `${engineLabel(row.provider)} ~${row.credits} cr`)
  if (ensemble.adjudication > 0) parts.push(`Merge ~${ensemble.adjudication} cr`)
  return parts.join(" · ")
}
